import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import { useProfile } from 'context/profile';
import usePagination from 'hooks/usePagination';
import { ClassificationApiName } from 'models/Classification';
import { clientClassificationsQueryKeys, indexClientClassifications } from 'services/api/clientClassifications';
import { useHealthPlansActions, useHealthPlansStore } from 'stores/healthPlansStore';
import { BodySmall } from 'styles/typography';

import AcceptedPlansTable from './AcceptedPlansTable';
import FilterBar from './FilterBar';

const AcceptedPlansPage = () => {
  const { profile } = useProfile();
  const pagination = usePagination();
  const search = useHealthPlansStore((state) => state.search);
  const sort = useHealthPlansStore((state) => state.sort);
  const { setSearch, setSort } = useHealthPlansActions();

  useEffect(
    // Reset search and sort when the page unmounts
    () => () => {
      setSearch('');
      setSort([{ id: 'name', desc: false }]);
    },
    [setSearch, setSort]
  );

  const serializeSort = (sort: SortingState): string => {
    return sort.map((s) => `${s.id} ${s.desc ? 'desc' : 'asc'}`).join(',');
  };

  const queryParams = useMemo(() => {
    return {
      pageSize: pagination.paging.pageSize,
      page: pagination.paging.page,
      sortBy: serializeSort(sort),
      classificationType: ClassificationApiName.HEALTH_PLAN,
      search,
    };
  }, [pagination.paging.page, pagination.paging.pageSize, sort, search]);

  const { data, isLoading } = useQuery({
    queryKey: clientClassificationsQueryKeys.index({
      ...queryParams,
    }),
    queryFn: ({ signal }) => {
      return indexClientClassifications({ ...queryParams }, signal);
    },
  });

  const formatDate = useCallback((date: string) => {
    const [year, month, day] = date.split('-');
    return `${month}/${day}/${year}`;
  }, []);

  return (
    <Page>
      <Page.Header>
        <FilterBar />
      </Page.Header>
      <Page.SubHeader>
        <TextContainer>
          <BodySmall>
            To ensure your referral workflow is optimized, please review and update the list of health plans your
            organization is contracted with. If {profile.actingClient.name} is no longer contracted with one of the
            listed plans, remove it to ensure your referral options remain accurate. If you need to add a health plan
            that isn't available,{' '}
            <a href='https://support.olio.health/hc/en-us' target='_blank'>
              submit a ticket to Olio support
            </a>
            .
          </BodySmall>
          {data?.meta.healthPlansLastUpdated.date && (
            <ItalicBodySmall>
              Last updated on {formatDate(data.meta.healthPlansLastUpdated.date)} by{' '}
              {data.meta.healthPlansLastUpdated.userName}
            </ItalicBodySmall>
          )}
        </TextContainer>
      </Page.SubHeader>
      <Page.Content>
        <AcceptedPlansTable data={data} isLoading={isLoading} pagination={pagination} />
      </Page.Content>
    </Page>
  );
};

const TextContainer = styled.div`
  margin-top: 20px;
`;

const ItalicBodySmall = styled(BodySmall)`
  font-style: italic;
  margin-top: 20px;
`;

export default AcceptedPlansPage;
